'use client';

import { Plus, MessageSquare, Trash2, AlertCircle } from '@/components/ui/icons';
import { cn } from '@/lib/utils';
import { useConversations, useCreateConversation, useDeleteConversation, type Conversation } from '@/hooks/use-conversations';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDistanceToNow } from 'date-fns';
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface ChatListPanelProps {
  projectId: string;
  activeChat?: string;
  onChatSelect?: (chatId: string | null) => void;
  onNewChat?: () => void;
}

export function ChatListPanel({ 
  projectId,
  activeChat, 
  onChatSelect,
  onNewChat 
}: ChatListPanelProps) {
  const { data, isLoading, isError } = useConversations(projectId);
  const createConversation = useCreateConversation(projectId);
  const deleteConversation = useDeleteConversation(projectId);
  const [hoveredChatId, setHoveredChatId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState<string | null>(null);

  const conversations = data?.conversations || [];

  const handleNewChat = async () => {
    try {
      const newConversation = await createConversation.mutateAsync({
        title: `New Chat - ${new Date().toLocaleDateString()}`,
      });
      onChatSelect?.(newConversation.id);
      onNewChat?.();
    } catch (error) {
      console.error('Failed to create conversation:', error);
      // Fallback to onNewChat callback
      onNewChat?.();
    }
  };

  const formatTimestamp = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return dateString;
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, chatId: string) => {
    e.stopPropagation(); // Prevent selecting the chat when clicking delete
    setChatToDelete(chatId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!chatToDelete) return;
    
    try {
      await deleteConversation.mutateAsync(chatToDelete);
      // If the deleted chat was active, clear the selection
      if (activeChat === chatToDelete) {
        onChatSelect?.(null);
      }
      setDeleteDialogOpen(false);
      setChatToDelete(null);
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      // Keep dialog open on error so user can try again
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setChatToDelete(null);
  };

  return (
    <div className="w-[320px] bg-white border-r border-gray-200 h-full flex flex-col">
      {/* New Chat Button */}
      <div className="p-4">
        <button
          onClick={handleNewChat}
          disabled={createConversation.isPending}
          className="w-full h-10 flex items-center justify-center gap-2 bg-brand-primary rounded-[10px] hover:bg-brand-primary/90 transition-colors text-white disabled:opacity-50"
        >
          <Plus className="w-5 h-5" />
          <span className="text-base">
            {createConversation.isPending ? 'Creating...' : 'New Chat'}
          </span>
        </button>
      </div>

      {/* Recent Chats */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 mb-2">
          <h3 className="text-base text-gray-700 tracking-tight px-3">
            Recent Chats
          </h3>
        </div>

        <div className="px-4 flex flex-col gap-1">
          {isLoading ? (
            // Loading skeleton
            <>
              {[1, 2, 3].map((i) => (
                <div key={i} className="p-3">
                  <Skeleton className="h-5 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              ))}
            </>
          ) : isError ? (
            // Error state
            <div className="p-4 text-center text-gray-500">
              <p className="text-sm">Unable to load chats</p>
            </div>
          ) : conversations.length === 0 ? (
            // Empty state
            <div className="p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm text-gray-500 mb-2">No conversations yet</p>
              <p className="text-xs text-gray-400">
                Start a new chat to ask questions about your project
              </p>
            </div>
          ) : (
            // Conversations list
            conversations.map((chat: Conversation) => {
              const isActive = activeChat === chat.id;
              const isHovered = hoveredChatId === chat.id;
              return (
                <div
                  key={chat.id}
                  className="relative group"
                  onMouseEnter={() => setHoveredChatId(chat.id)}
                  onMouseLeave={() => setHoveredChatId(null)}
                >
                  <button
                    onClick={() => onChatSelect?.(chat.id)}
                    className={cn(
                      'w-full text-left p-3 rounded-[10px] transition-colors pr-10',
                      isActive 
                        ? 'bg-[#e0e7ff]' 
                        : 'hover:bg-gray-100'
                    )}
                  >
                    <p className={cn(
                      'text-base tracking-tight mb-1 truncate',
                      isActive ? 'text-[#312c85]' : 'text-gray-900'
                    )}>
                      {chat.title}
                    </p>
                    <p className={cn(
                      'text-sm tracking-tight',
                      isActive ? 'text-[#312c85]/70' : 'text-gray-500'
                    )}>
                      {formatTimestamp(chat.createdAt)}
                    </p>
                  </button>
                  {/* Delete button - appears on hover */}
                  <button
                    onClick={(e) => handleDeleteClick(e, chat.id)}
                    disabled={deleteConversation.isPending}
                    className={cn(
                      'absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-[6px] transition-opacity',
                      'text-gray-400 hover:text-red-600 hover:bg-red-50',
                      'disabled:opacity-50',
                      isHovered ? 'opacity-100' : 'opacity-0'
                    )}
                    title="Delete conversation"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px] rounded-[14px]">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <DialogTitle className="text-lg font-medium text-gray-900">
                Delete Conversation
              </DialogTitle>
            </div>
            <DialogDescription className="text-base text-gray-600 pt-2">
              Are you sure you want to delete this conversation? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row gap-2 sm:justify-end pt-4">
            <button
              onClick={handleCancelDelete}
              disabled={deleteConversation.isPending}
              className="h-10 px-4 border border-gray-300 rounded-[10px] text-base text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              disabled={deleteConversation.isPending}
              className="h-10 px-4 bg-red-600 text-white rounded-[10px] text-base hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {deleteConversation.isPending ? 'Deleting...' : 'Delete'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
